import { computed, inject, ref } from 'vue';
import { useConfirm } from 'primevue/useconfirm';
import {
  useEndpoint,
  useFetch,
  useLocalStorage,
  useStringFunctions,
  useToastNotifications,
  useUtilities,
} from '@apparatix/composables';
import { useOpportunityStore } from '@apparatix/stores/useOpportunityStore';
import {
  AssociationsResponse,
  OpportunityType,
  SavedViewPreference,
  ViewFormat,
  InitialStateResponse,
  LaneCardProps,
  LaneUpdate,
  GenericApiResponse,
  OpportunityBase,
  OpportunityFilter,
  User,
  OpportunityFilterPredicate,
  Swimlane,
  OpportunityResponse,
  OpportunityUpdatePayload,
  AssociationContact,
  OpportunityStatusUpdate,
  PayloadBase,
  OpportunityDuplicateResponse,
  Opportunity,
} from '@apparatix/types/Leads';
import axios from 'axios';
import { button as greenButton } from '@apparatix/theme/Green';
import type { User as SystemUser } from '@apparatix/types';
import type { DocumentIdAndName } from '@apparatix/types/Documents';
import { ContactLabel } from '@apparatix/types/Account';
import { MenuItem } from 'primevue/menuitem';

type OpportunityUpdateArgs = { id: number };
type OpportunityHasContractResponse = { data: { hasContract: boolean }};

export const useOpportunities = () => {
  const {
    getItem, setItem, removeItem,
  } = useLocalStorage();
  const store = useOpportunityStore();
  const _fetch = useFetch();
  const toast = useToastNotifications();
  const confirmService = useConfirm();

  const opportunityType = ref<OpportunityType>();

  const { isEmptyString, ensureString } = useStringFunctions();
  const { isValidNumber } = useUtilities();

  const currentUser = inject<SystemUser>('current-user', undefined);

  const opportunityDetailEndpoint = useEndpoint<OpportunityResponse>({
    type: 'GET',
    url: '',
    successField: 'data',
    onSuccess: response => {
      const { opportunity } = response.data;
      store.isDetailLoading[opportunity.type] = false;

      store.selectedOpportunity[opportunity.type] = response.data;
      store.canGenerateProposalOnLead[opportunity.type] = response.data.permissions.some((permission) => permission.tag === 'canGenerateProposalOnLead');
      store.canViewAudience[opportunity.type] = response.data.permissions.some((permission) => permission.tag === 'canSeeAudience');

      const existsInStore = Boolean(store.opportunities[opportunity.type].find(o => o.id === opportunity.id));
      if (!existsInStore) {
        store.addOpportunity(opportunity);
      }
    },
    toast: { showOnSuccess: false },
  });

  const duplicateEndpoint = useEndpoint<OpportunityDuplicateResponse>({
    type: 'POST',
    successField: 'data',
    toast: {
      showOnSuccess: false,
    },
  });

  const opportunityHasContractEndpoint = useEndpoint<OpportunityHasContractResponse>({
    type: 'GET',
    successField: 'data',
    toast: {
      showOnSuccess: false,
      showOnError: false,
    },
  });

  const markOpportunityWonEndpoint = useEndpoint<GenericApiResponse>({
    type: 'PUT',
    successField: 'success',
    toast: {
      showOnSuccess: false,
      showOnError: false,
    },
  });

  const updateEndpoint = useEndpoint<GenericApiResponse>({
    type: 'PUT',
    url: '/crm/opportunities',
    successField: 'success',
    onSuccess: async (_: GenericApiResponse, args: OpportunityUpdateArgs) => {
      if (!args.id) return;
      const response = await _fetch.get<OpportunityResponse>(`/crm/opportunities/${args.id}`);
      if (!response?.data?.opportunity) return;
      const { opportunity } = response.data;
      store.updateOpportunity(opportunity.type, opportunity);

      // Don't update the store if we are calling the update from a state where the updated record isn't what is in the store
      if (store.selectedOpportunity[opportunity.type]?.opportunity.id !== response.data.opportunity.id) return;
      store.selectedOpportunity[opportunity.type] = response.data;
    },
    toast: { showOnSuccess: false },
  });

  const combinedLanes = computed(() => [...store.swimlanes['Opportunity'], ...store.swimlanes['Lead']]);

  const saveViewPreference = (view: ViewFormat, type: OpportunityType) => {
    const pref: SavedViewPreference = {
      key: type === 'Lead' ? 'leads:view-preference' : 'opportunities:view-preference',
      value: {
        swimlanes: view === 'swimlanes',
        grid: view === 'table',
      },
    };

    setItem(pref);
  };

  const getSavedViewPreference = (type: OpportunityType): ViewFormat => {
    const storageKey = type === 'Lead' ? 'leads:view-preference' : 'opportunities:view-preference';
    const lastViewUsed = getItem<{ swimlanes: boolean, grid: boolean }>(storageKey);

    if (lastViewUsed?.grid) {
      return 'table';
    }

    return 'swimlanes';
  };

  const getSavedLanesVisible = (type: OpportunityType) => {
    const key = `${type.toLowerCase()}:lanes-visible`;
    return getItem<Swimlane[]>(key);
  };

  const saveLanesVisible = (type: OpportunityType, lanes: Swimlane[]) => {
    const key = `${type.toLowerCase()}:lanes-visible`;
    setItem({
      key,
      value: lanes,
    });
  };

  const updateSelectedView = (type: OpportunityType, view: ViewFormat) => {
    saveViewPreference(view, type);
    store.viewFormat[type] = view;
  };

  const uploadFile = async (id: number, files: File[], type = 'opportunities'): Promise<boolean> => {
    const formData = new FormData();
    Array.from(files).forEach((file) => {
      formData.append('file[]', file);
    });

    try {
      await axios.post(`/crm/${type}/${id}/files`, formData);

      return true;
    } catch {
      return false;
    }
  };

  const getOpportunityHasContract = async (id: number): Promise<boolean> => {
    if (!id) return false;

    opportunityHasContractEndpoint.setEndpointUrl(`/crm/opportunities/${id}/has-contract`);
    const response = await opportunityHasContractEndpoint.getResults();
    return Boolean(response?.data?.hasContract);
  };


  const loadOpportunities = async (type: OpportunityType, priority: RequestPriority, state = 'active') => {
    const { data } = await _fetch.post<InitialStateResponse>('/crm/opportunities/filter', { filter: { type: { key: type }, state: state }}, false, priority);
    const {
      opportunities, swimlanes, options, permissions,
    } = data;

    const cleanOpportunities = sanitizeOpportunities(opportunities);

    cleanOpportunities
      .forEach(opportunity => {
        const user = store.usersWithOpportunities[type].find(u => u.id === opportunity.ownerId);
        if (!user) {
          store.usersWithOpportunities[type].push({
            id: opportunity.ownerId,
            name: opportunity.owner,
            opportunityCount: 1,
          });
        } else {
          user.opportunityCount += 1;
        }
      });

    swimlanes.forEach(lane => {
      if (!lane.type) {
        lane.type = type;
      }
    });

    options.allSwimlanes.forEach(lane => {
      if (!lane.type && lane.metaData.toLowerCase() === type.toLowerCase()) {
        lane.type = type;
      }
    });

    options.availableStatuses.forEach(lane => {
      if (!lane.type) {
        lane.type = type;
      }
    });

    options.possibleStatuses.forEach(lane => {
      if (!lane.type) {
        lane.type = type;
      }
    });

    store.setOpportunities(type, cleanOpportunities);
    store.swimlanes[type] = swimlanes;
    store.options[type] = options;
    store.hasProjectionsManagerView = permissions.some((permission) => permission.tag === 'hasProjectionsManagerView');

    if (type === 'Opportunity') {
      store.currentlySelectedUsersForProjections = store.options['Opportunity'].users.filter(user => user.fullName !== 'Unprotected');
      store.currentlySelectedOfficesForProjections = store.options['Opportunity'].offices;
    }

    if (currentUser) {
      const { officeId } = options.users.find(user => user.id === currentUser.id);
      store.currentUser = {
        id: currentUser.id,
        fullName: currentUser.displayName,
        userId: currentUser.username,
        officeId: officeId,
        isActive: currentUser.isActive,
      };
    }

    const savedVisibleLanes = getSavedLanesVisible(type);

    if (savedVisibleLanes) {
      // If the schema of a lane has changed, it could lead to some bugs where the JSON of the stored lanes doesn't match the actual schema,
      // so in this case we just delete the saved lanes. TODO: maybe give the user a heads up this has happened?
      const shouldInvalidateSave = shouldInvalidateSavedLanes(savedVisibleLanes, swimlanes);
      if (shouldInvalidateSave) {
        removeItem(`${type.toLowerCase()}:lanes-visible`);
        store.selectedVisibleLanes[type] = swimlanes;
      } else {
        store.selectedVisibleLanes[type] = savedVisibleLanes;
      }
    } else {
      store.selectedVisibleLanes[type] = swimlanes;
    }

    store.stateReady[type] = true;
  };

  const sanitizeOpportunities = (opportunities: OpportunityBase[]) => opportunities.filter(opportunity => !isEmptyString(opportunity.owner) && isValidNumber(opportunity.ownerId));

  const getViewFormatForType = (type: OpportunityType): ViewFormat => store.viewFormat[type];

  const shouldInvalidateSavedLanes = (savedLanes: Swimlane[], realLanes: Swimlane[]) => {
    let shouldInvalidate = false;

    savedLanes.forEach(savedLane => {
      const realLane = realLanes.find(rl => rl.id === savedLane.id);

      // Check to make sure the saved lane still exists in the database
      if (!realLane) {
        shouldInvalidate = true;
        return;
      }

      // Check to make sure saved lane and database lane contain identical fields
      const actualKeys = Object.keys(savedLane).filter(key => Object.hasOwn(savedLane, key)).sort();
      const expectedKeys = Object.keys(realLane).filter(key => Object.hasOwn(realLane, key)).sort();
      // Quick check to make sure they have the same number of fields
      if (actualKeys.length !== expectedKeys.length) {
        shouldInvalidate = true;
        return;
      }
      // Then check to make sure field names are all exactly the same
      if (!actualKeys.every((value, idx) => value === expectedKeys[idx])) {
        shouldInvalidate = true;
        return;
      }
    });

    return shouldInvalidate;
  };

  const loadInitialState = () => {

    const opportunityPriority: RequestPriority = store.currentView === 'Opportunity' ? 'high' : 'low';

    const leadPriority: RequestPriority = store.currentView === 'Lead' ? 'high' : 'low';

    // We intentionally do not await loading the initial state so that loading data for inactive views doesn't block UI rendering
    if (!store.stateReady['Opportunity']) {
      loadOpportunities('Opportunity', opportunityPriority);
    }

    if (!store.stateReady['Lead']) {
      loadOpportunities('Lead', leadPriority);
    }

    store.viewFormat['Opportunity'] = getSavedViewPreference('Opportunity');
    store.viewFormat['Lead'] = getSavedViewPreference('Lead');
  };

  const findOpportunityById = (type: OpportunityType, id: number): OpportunityBase | null => store.opportunities[type].find(opportunity => opportunity.id === id);

  // Returns a rendered swimlane based on ID
  const getLaneById = (id?: number) => {
    if (!id) return null;
    return combinedLanes.value?.find(lane => lane.id === id);
  };

  // Returns a status based on ID
  const getStatusById = (type: OpportunityType, id?: number) => {
    if (!id) return null;
    return store.options[type].possibleStatuses.find(lane => lane.id === id);
  };

  const getLanePosition = (type: OpportunityType, id: number) => store.options[type].availableStatuses.findIndex(lane => lane.id === id);

  const getLaneTitleById = (id: number) => {
    const lane = getLaneById(id);

    return lane?.status ?? '';
  };

  const getCompletedOpportunityStatus = () => {
    const sorter = (a: Swimlane, b: Swimlane) => {
      const aOrder = Number(a.order);
      const bOrder = Number(b.order);

      if (aOrder < bOrder) return -1;
      if (aOrder === bOrder) return 0;
      return 1;
    };

    const sortedStatuses = store.swimlanes['Opportunity'].toSorted(sorter);

    return sortedStatuses.pop();
  };

  const duplicateCampaign = async (campaignId: number): Promise<number | undefined> => {
    const url = `/crm/opportunities/duplicate/${campaignId}`;
    duplicateEndpoint.setEndpointUrl(url);
    const response = await duplicateEndpoint.getResults();
    if (response?.data?.id) {
      return response.data.id;
    }

    return undefined;
  };

  const isOpportunityCompleted = (opportunity: OpportunityBase) => {
    if (!opportunity?.id) return false;
    const { status } = opportunity;
    return store.options[opportunity.type].possibleStatuses.find(lane => lane.status === status)?.state === 'completed';
  };

  const getOpportunityCountForLane = (type: OpportunityType, laneId: number) => store.opportunities[type].filter(opportunity => opportunity.swimlaneId === laneId)?.length ?? 0;

  const updateLane = async (update: LaneUpdate, reasons?: number[]): Promise<GenericApiResponse> => {
    if (update.oldLane === update.newLane) {
      return { success: true };
    }

    const lane = getLaneById(update.oldLane);
    const opportunity = findOpportunityById(lane.type, update.leadId);

    opportunity.swimlaneId = update.newLane;
    opportunity.status = store.options[lane.type].possibleStatuses.find(item => item.id === update.newLane)?.status ?? opportunity.status;

    let payload: PayloadBase<OpportunityStatusUpdate[]>;
    payload = { data: { opportunity: [{ id: update.leadId, status: update.newLane }]}};
    if (reasons?.length) {
      payload = {
        data: {
          opportunity: [{
            id: update.leadId, status: update.newLane, opportunityLostReasons: reasons,
          }],
        },
      };
    }


    const result = await _fetch.put<GenericApiResponse, PayloadBase<OpportunityStatusUpdate[]>>('crm/opportunities', payload, false, 'low');
    if (!result?.success) {
      opportunity.swimlaneId = update.oldLane;
      return result ?? { success: false };
    }

    return { success: true };
  };

  const markOpportunityWon = async (update: LaneUpdate): Promise<GenericApiResponse> => {
    if (update.oldLane === update.newLane) {
      return { success: true };
    }

    const lane = getLaneById(update.oldLane);
    const opportunity = findOpportunityById(lane.type, update.leadId);

    const previousLaneId = opportunity.swimlaneId;

    markOpportunityWonEndpoint.setEndpointUrl(`/crm/opportunities/${update.leadId}/mark-won`);
    const result = await markOpportunityWonEndpoint.getResults();
    if (!result?.success) {
      opportunity.swimlaneId = previousLaneId;
      return result ?? { success: false };
    }

    // We need to use the Lost id - Recently Won/Lost SwimlaneId matches it
    opportunity.swimlaneId = store.options[lane.type].possibleStatuses.find(item => item.status === 'Lost')?.id;
    opportunity.status = store.options[lane.type].possibleStatuses.find(item => item.status === 'Won')?.status;

    return { success: true };
  };

  const openDetailFlyout = async (type: OpportunityType, opportunityId: number) => {
    store.isDetailLoading[type] = true;
    opportunityDetailEndpoint.setEndpointUrl(`/crm/opportunities/${opportunityId}`);
    await opportunityDetailEndpoint.makeRequest();
  };

  const closeDetailFlyout = (type: OpportunityType) => {
    store.shouldShowDetail[type] = false;
    store.selectedOpportunity[type] = null;
    store.campaignAccounts = [];
    store.campaignContacts = [];
  };

  const updateActiveDetailFlyoutVisibility = (type: OpportunityType, shouldShow: boolean) => {
    store.shouldShowDetail[type] = shouldShow;
    if (!shouldShow) {
      store.selectedOpportunity[type] = null;
    }
  };

  const addFilter = (filter: OpportunityFilter, updateFilterIfExists = true) => {
    const idx = store.activeFilters[filter.type].findIndex(f => f.name === filter.name);
    if (idx === -1) {
      store.activeFilters[filter.type].push(filter);
    } else {
      if (updateFilterIfExists) {
        store.activeFilters[filter.type][idx] = filter;
      }
    }
  };

  const removeFilter = (type: OpportunityType, filterName: string) => {
    const idx = store.activeFilters[type].findIndex(filter => filter.name === filterName);

    if (idx === -1) {
      return;
    }

    store.activeFilters[type].splice(idx, 1);
  };

  const runGlobalSearch = (type: OpportunityType, query: string, minLength = 3) => {
    const searchFilterName = `global-search-${type}-filter`;

    const cleanQuery = query?.toLowerCase().trim();

    if (isEmptyString(cleanQuery, true) || cleanQuery?.length < minLength) {
      removeFilter(type, searchFilterName);
      store.lastGlobalSearchQuery[type] = '';
      return;
    }

    if (cleanQuery === store.lastGlobalSearchQuery[type]) {
      return;
    }

    const filter: OpportunityFilter = {
      type: type,
      name: searchFilterName,
      predicate: opportunity => opportunity.name.toLowerCase().includes(cleanQuery) || opportunity.company?.toLowerCase().includes(cleanQuery),
    };

    addFilter(filter);
    store.lastGlobalSearchQuery[type] = cleanQuery;
  };

  const addOrRemoveOwnerFromFilterGroup = (type: OpportunityType, owner: User) => {
    const filterName = `global-${type}-owner-filter`;

    const idx = store.currentlySelectedOwnersForFilter[type].findIndex(user => user.id === owner.id);
    if (idx === -1) {
      store.currentlySelectedOwnersForFilter[type].push(owner);
    } else {
      store.currentlySelectedOwnersForFilter[type].splice(idx, 1);
    }

    if (store.currentlySelectedOwnersForFilter[type].length < 1) {
      removeFilter(type, filterName);
      return;
    }

    const predicate: OpportunityFilterPredicate =
      opportunity => store.currentlySelectedOwnersForFilter[type].some(user => opportunity.ownerId === user.id);

    addFilter({
      type,
      name: filterName,
      predicate,
    });
  };

  const isOwnerSelectedForFilter = (type: OpportunityType, owner: User) =>
    store.currentlySelectedOwnersForFilter[type].findIndex(user => user.id === owner.id) !== -1;

  const calculateLaneWidth = (container: HTMLDivElement, laneCount: number): [string, string] => {
    if (!container) return [null, null];

    let idealLaneCount = getTargetNumberOfVisibleLanes();
    if (laneCount > idealLaneCount && isLarge()) {
      if (laneCount > 5) {
        idealLaneCount = 5;
      } else {
        idealLaneCount = laneCount;
      }
    }

    const gapRems = '0.75rem';
    const containerStyle = getComputedStyle(container);
    const containerWidth = parseFloat(containerStyle.width);
    const paddingLeft = parseFloat(containerStyle.paddingLeft);
    const paddingRight = parseFloat(containerStyle.paddingRight);
    const gapSize = getAbsoluteSizeForRem(gapRems);
    const gapPadding = (laneCount - 1) * gapSize;
    const availableWidth = containerWidth - paddingLeft - paddingRight - gapPadding;

    const targetWidth = availableWidth / idealLaneCount;

    return [`${getRemsForAbsoluteSize(targetWidth)}rem`, gapRems];
  };

  const getAbsoluteSizeForRem = (remValue: string): number => {
    const tempElement = document.createElement('div');
    tempElement.style.fontSize = remValue;
    document.body.appendChild(tempElement);

    const absoluteValue = parseFloat(getComputedStyle(tempElement).fontSize);
    document.body.removeChild(tempElement);

    return absoluteValue;
  };

  const getRemsForAbsoluteSize = (absoluteSize: number) => {
    const oneRem = getAbsoluteSizeForRem('1rem');

    return absoluteSize / oneRem;
  };

  const getTargetNumberOfVisibleLanes = () => {
    if (isSmall()) {
      return 1;
    }
    if (isMedium()) {
      return 2;
    }

    return 4;
  };

  const isSmall = () => window.innerWidth <= 640;
  const isMedium = () => window.innerWidth > 640 && window.innerWidth <= 1100;
  const isLarge = () => window.innerWidth > 1100;

  const getOpportunityOwnerOrDefault = (type: OpportunityType, ownerId?: number) => {
    if (!ownerId || store.currentUser.id === ownerId) return store.currentUser;
    return store.options[type].users?.find(user => user.id === ownerId) ?? store.currentUser;
  };

  const getConfidence = (type: OpportunityType, ci?: number) => {
    // ci is undefined on create - DB field is NOT NULLABLE default 0
    if (!ci && ci !== 0) return null;
    // TODO: backend should return consistent type
    const ciValue = ensureString(ci);
    return store.options[type].confidenceIntervals?.find(interval => interval.confidencePercentage === ciValue);
  };

  const getInterval = (type: OpportunityType, intervalValue?: string) => {
    if (!intervalValue) return store.options[type].campaignIntervals?.find(interval => interval.selectionValue === store.options[type].defaultInterval);
    return store.options[type].campaignIntervals?.find(interval => interval.selectionValue === intervalValue);
  };

  const updateOpportunity = async (payload: OpportunityUpdatePayload, refetchDetail: boolean = true) => {
    if (refetchDetail) {
      await updateEndpoint.makeRequest(payload, { id: payload.data.opportunity[0].id });
    } else {
      await updateEndpoint.makeRequest(payload);
    }
  };

  const standardizeObjectForPutRequest = (obj: any, deep: boolean = false) => {
    if (!obj) return;
    Object.keys(obj).filter(key => Object.hasOwn(obj, key)).forEach(key => {
      if (typeof obj[key] === 'object') {
        if (deep) {
          standardizeObjectForPutRequest(obj[key], true);
        }
        if (!obj[key]) return;
        if (Object.hasOwn(obj[key], 'id')) {
          obj[key] = obj[key].id;
        }
      }
    });
  };

  const hasPrimaryContact = (contacts: AssociationContact[]) => {
    if (!Array.isArray(contacts)) return false;
    const labels = contacts.flatMap(contact => contact.contactLabels);
    return labels?.filter(label => label?.contactLabelDisplayName === 'Primary')?.length === 1;
  };

  const onAfterLeadConverted = async (opportunity: Partial<Opportunity> & { statusId: number }) => {
    if (!opportunity.id) return;
    const lead = store.opportunities['Lead'].find(l => l.id === opportunity.id);
    const defaultOpportunityStatus = store.swimlanes['Opportunity'][0];
    const leadStatus = lead.swimlaneId ?? lead.statusId;

    const status = store.swimlanes['Opportunity'].find(lane => lane.id === leadStatus) ?? defaultOpportunityStatus;

    lead.status = status.status;
    lead.swimlaneId = status.id;
    lead.statusId = status.id;
    lead.type = 'Opportunity';
    lead.name = opportunity.name;

    store.removeOpportunity('Lead', lead.id);
    store.selectedCampaignId['Opportunity'] = lead.id;
    store.shouldShowDetail['Opportunity'] = true;
    toast.showSuccess('Your Lead was successfully converted to an Opportunity', 'Lead Converted');
  };

  const findUserById = (userId: number) => userId ? store.options['Opportunity'].allUsers?.find(user => user.id === userId) : undefined;

  const getUserByIdOrDefault = (userId?: number) => {
    if (!userId) {
      return store.currentUser;
    }

    const user = findUserById(userId);
    if (user) {
      return user;
    }

    return store.currentUser;
  };

  const findPrimaryContact = <T extends { contactLabels?: ContactLabel[] }>(contacts: T[]) => contacts?.find(
    contact => contact.contactLabels?.some(label => label.contactLabelId === 1),
  );

  const findInactiveStatus = (type: OpportunityType) => store.options[type].availableStatuses?.find(status => status.state === 'inactive');

  const buildAddActivity = (currentView, object, showModal: (type: string, view: string, payload: any) => void): MenuItem => ({
    label: 'Add Activity',
    icon: 'pi pi-calendar',
    command: () => {
      showModal('Task', currentView, { id: object.id, name: object.name, type: object.type });
    },
  });

  const buildAddReminder = (currentView, object, showModal: (type: string, view: string, payload: any) => void): MenuItem => ({
    label: 'Add Reminder',
    icon: 'pi pi-bell',
    command: () => {
      showModal('Reminder', currentView, { id: object.id, name: object.name, type: object.type });
    },
  });

  const buildConvertToOpportunity = (id: number, type: OpportunityType): MenuItem => ({
    label: 'Convert to Opportunity',
    icon: 'pi pi-pencil',
    class: 'pv-menu-item-primary-text',
    command: () => handleConvertLeadToOpportunity(id, type),
  });

  const buildProposalItem = (documents: DocumentIdAndName[]): MenuItem => ({
    id: documents.length === 0 ? 'createProposal' : 'openProposal',
    label: documents.length === 0 ? 'Create Proposal' : 'Open Proposal',
    icon: 'pi pi-pen-to-square',
  });

  const { makeRequest, setEndpointUrl } = useEndpoint<AssociationsResponse>({
    type: 'GET',
    successField: 'data',
    onSuccess: response => {
      store.campaignAccounts = response.data.accounts?.filter(account => account.c2Status !== 'Inactive'); // Filters out auto-generated accounts
      store.campaignContacts = response.data.contacts;
    },
    toast: { showOnSuccess: false },
  });

  // Expedited method from hamburger menu
  const handleConvertLeadToOpportunity = async (id: number, type: OpportunityType) => {
    opportunityType.value = type;
    store.selectedCampaignId[type] = id;
    store.createOpportunityContext = 'convert-lead';
    store.shouldShowDetail[type] = false;
    setEndpointUrl(`/crm/opportunities/${id}/associations`);
    /*
    * Not thrilled with this duplicate call
    * We hit this endpoint in AssociationsEditor.vue
    * When we make the getAccounts() from CreateCampaignWizard.vue - store.campaignAccounts and
    *   store.campaignContacts are considered empty without hitting this endpoint here
    */
    await makeRequest();
    await openDetailFlyout('Lead', id);
    store.showOpportunityCreationModal = true;
  };

  const buildMoveToInactiveMenuItem = (type: OpportunityType, getOpportunityId: () => number): MenuItem => {
    const inactiveStatus = findInactiveStatus(type);
    if (!inactiveStatus) {
      return undefined;
    }

    return {
      label: `Move to ${inactiveStatus.status}`,
      icon: 'pi pi-box',
      command: () => confirmService.require({
        group: 'defer-campaign',
        message: `This ${type} will be ${inactiveStatus.status}. Do you want to Continue?`,
        position: 'center',
        icon: 'pi pi-exclamation-triangle',
        accept: () => {
          const payload: OpportunityUpdatePayload = {
            data: {
              opportunity: [
                {
                  id: getOpportunityId(),
                  status: String(inactiveStatus.id),
                },
              ],
            },
          };
          updateOpportunity(payload);
        },
        acceptLabel: 'Yes',
        rejectLabel: 'No',
        rejectClass: 'p-button-secondary',
        acceptProps: {
          dt: greenButton,
        },
      }),
    };
  };

  const isOpportunityActive = <T extends { status: string } = OpportunityBase>(type: OpportunityType, opportunity: T) =>
    store.options[type].possibleStatuses?.find(lane => lane.status === opportunity?.status)?.state === 'active';

  const isOpportunityWon = <T extends { statusId: number, type: OpportunityType } = OpportunityBase>(opportunity: T) => {
    if (!opportunity) return false;
    const status = store.options[opportunity.type].possibleStatuses?.find(s => s.id === opportunity.statusId);

    return status?.state?.toLowerCase() === 'completed' && status?.status?.toLowerCase() === 'won';
  };

  const isOpportunityLost = <T extends { statusId: number, type: OpportunityType } = OpportunityBase>(opportunity: T) => {
    if (!opportunity) return false;

    const status = store.options[opportunity.type].possibleStatuses?.find(s => s.id === opportunity.statusId);
    return status?.status?.toLowerCase() === 'lost';
  };

  const getAudienceSelectionUrl = (opportunityId: number) => `/apx/TAG/campaigns/campaignAudienceSelection.html?id=${opportunityId}`;

  const getGeoSelectionUrl = (opportunityId: number) => `/apx/TAG/campaigns/campaignGeographicSelection.html?id=${opportunityId}`;

  const createProposal = (opportunity: LaneCardProps|OpportunityBase) => {
    const { id, accountId } = opportunity;

    window.open(`/apx/proposalTool/index-continue.html?advertiser=${accountId}&cn=${id}`, '_blank');
  };

  const openProposal = (documentId: number, opportunity: LaneCardProps|OpportunityBase) => {
    const url = `/apx/proposalTool/index-continue.html?advertiser=${opportunity.accountId}&tagid=&pid=${documentId}`;

    window.open(url, '_blank');
  };

  const getProposalSplitItems = (documents: DocumentIdAndName[], opportunity: LaneCardProps|OpportunityBase): MenuItem[] => {
    const baseItems: MenuItem[] = [];

    if (documents.length > 1) {
      baseItems.push(...documents.map((doc: DocumentIdAndName) => ({
        label: isEmptyString(doc.name) ? doc.id.toString() : doc.name,
        icon: 'pi pi-external-link',
        command: () => openProposal(doc.id, opportunity),
      })),
      );
    }

    baseItems.push({ separator: true });

    baseItems.push({
      label: 'Create Proposal',
      icon: 'pi pi-plus',
      command: () => createProposal(opportunity),
      class: 'pv-menu-item-primary-text',
    });

    return baseItems;
  };

  return {
    addOrRemoveOwnerFromFilterGroup,
    buildAddActivity,
    buildAddReminder,
    buildConvertToOpportunity,
    buildMoveToInactiveMenuItem,
    buildProposalItem,
    calculateLaneWidth,
    closeDetailFlyout,
    createProposal,
    duplicateCampaign,
    findOpportunityById,
    findPrimaryContact,
    findUserById,
    getAudienceSelectionUrl,
    getCompletedOpportunityStatus,
    getConfidence,
    getGeoSelectionUrl,
    getInterval,
    getLaneById,
    getLanePosition,
    getLaneTitleById,
    getOpportunityCountForLane,
    getOpportunityOwnerOrDefault,
    getOpportunityHasContract,
    getProposalSplitItems,
    getSavedViewPreference,
    getStatusById,
    getUserByIdOrDefault,
    getViewFormatForType,
    hasPrimaryContact,
    isOpportunityActive,
    isOpportunityCompleted,
    isOpportunityLost,
    isOpportunityWon,
    isOwnerSelectedForFilter,
    loadInitialState,
    loadOpportunities,
    onAfterLeadConverted,
    openDetailFlyout,
    openProposal,
    runGlobalSearch,
    saveLanesVisible,
    saveViewPreference,
    standardizeObjectForPutRequest,
    markOpportunityWon,
    updateActiveDetailFlyoutVisibility,
    updateLane,
    updateOpportunity,
    updateSelectedView,
    uploadFile,
  };
};
